// imports
import React, {
  useRef, useState,
  useEffect, DragEvent, useMemo
} from 'react';
import { AnyAction } from '@reduxjs/toolkit';
import { useSelector, useDispatch } from 'react-redux';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';

import { ROUTES } from '../../constants';
import GameLayout from '../../layouts/game';
import TabView from '../../molecules/tabView';
import HeroList from '../../molecules/list/hero';
import { QUEST_UTILS } from '../../common/utils';
import { HeroNFT } from '../../repositories/web3/types';
import { Quest, QuestCost, QuestStatus } from '../../common/types';
import QuestDetailView from '../../molecules/tabView/sidebar/questDetailView';
import {
  selectConfig,
  selectUserBackpack, selectUserQuests, selectUserHeroes
} from '../../repositories/store/slices';
import { fetchHeroes, fetchQuestsProgress } from '../../repositories/store/thunks';

// exports
export default function QuestCrew(): JSX.Element {
  // hooks configuration
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const draggedHero = useRef<HeroNFT>();
  const [crew, setCrew] = useState<number[]>([0, 0, 0]);
  const { state: { quest, slotIdx: selectedSlot } }: { state: { quest: Quest; slotIdx: number } } = useLocation();
  const [slotIdx, setSlotIdx] = useState<number>(selectedSlot || 0);
  const { world } = useSelector(selectConfig);
  const { items } = useSelector(selectUserBackpack);
  const { nfts: heroNFTs, fetchStatus: fetchHeroesStatus } = useSelector(selectUserHeroes);
  const { data: questsData, fetchStatus: fetchQuestsStatus } = useSelector(selectUserQuests);
  // componentDidMount
  useEffect(() => {
    if (fetchHeroesStatus === 'idle') {
      dispatch(fetchHeroes() as unknown as AnyAction);
    } else if (fetchHeroesStatus === 'loading') { setCrew([0, 0, 0]); }
    if (quest && questsData[quest.id]?.status === QuestStatus.inProgress) {
      navigate(ROUTES.QUESTS, { state: { pinId: quest!.pinId, defaultTab: 'in-progress', lastSelectedQuestId: quest.id } });
    }
    if (fetchQuestsStatus === 'idle') { dispatch(fetchQuestsProgress() as unknown as AnyAction); }
  }, [quest, fetchHeroesStatus, fetchQuestsStatus, questsData, navigate, dispatch]);

  const [crewTraitMatching, questTraitMatching] = useMemo(() => {
    if (!quest.traits) { return [[], []]; }
    return QUEST_UTILS.getQuestTraitMatching(quest.traits, crew, heroNFTs);
  }, [quest, crew, heroNFTs]);

  const validState = quest?.name;
  if (!validState) { return <Navigate to={ROUTES.GAME} />; }

  const insufficientResources: QuestCost[] = [];
  quest.cost?.forEach((cost) => { if (items[cost.itemId] < cost.amount) { insufficientResources.push(cost); } });

  // private methods
  const heroRemovedCB = (slotNumber: number): void => {
    const updatedHeroes = [...crew];
    updatedHeroes[slotNumber] = 0;
    setCrew(updatedHeroes);
  };
  const heroDraggedCB = (hero?: HeroNFT): void => { draggedHero.current = hero; };
  const placeHero = (selectedHero?: HeroNFT, pSlot?: number): void => {
    if (!selectedHero) return;
    const updatedCrew = [...crew];
    const { tokenId } = selectedHero;
    const alreadySelectedIdx = crew.findIndex((heroId) => tokenId === heroId);
    if (alreadySelectedIdx !== -1) { updatedCrew[alreadySelectedIdx] = 0; }
    updatedCrew[pSlot !== undefined ? pSlot : slotIdx] = tokenId;
    setCrew(updatedCrew);
  };
  const heroClickedCB = (selectedHero?: HeroNFT): void => {
    if (!selectedHero?.locked) { placeHero(selectedHero); }
  };
  const onDroppedCB = (e: DragEvent<HTMLDivElement>, pSlot: number): void => {
    e.preventDefault();
    placeHero(draggedHero.current, pSlot);
  };
  const navigateBack = (): void => navigate(ROUTES.QUESTS, { state: { pinId: quest.pinId, lastSelectedQuestId: quest.id } });
  const selectedCrew = (quest.status !== QuestStatus.available) ? quest.crew : crew;

  const questDetailView = (
    <QuestDetailView
      quest={quest}
      crew={selectedCrew}
      currentSlot={slotIdx}
      questTraitMatching={questTraitMatching}
      insufficientResources={insufficientResources}
      setSlot={setSlotIdx}
      onDroppedCB={onDroppedCB}
      heroDraggedCB={heroDraggedCB}
      heroRemovedCB={heroRemovedCB}
    />
  );
  const regionName = (quest?.pinId && world.pins && world.pins[quest!.pinId])
    ? world.regions[world.islands[world.pins[quest!.pinId].islandId].regionId].name : '';
  // render
  return (
    <GameLayout
      backButtonText={regionName}
      backBtnClickedCB={navigateBack}
      items={quest.cost && quest.cost.map((cost) => cost.itemId)}
    >
      <TabView sidebar={questDetailView}>
        <HeroList
          disableLocked
          selectedCrew={crew}
          slotTraits={quest.traits[slotIdx]}
          crewTraitMatching={crewTraitMatching}
          heroClickedCB={heroClickedCB}
          heroDraggedCB={heroDraggedCB}
        />
      </TabView>
    </GameLayout>
  );
}
